<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import type { DashboardWidget } from '../../models/widget.types'
import type { WidgetSheet } from '../../models/widgetExport.types'
import {
  widgetDefaultFilters,
  widgetToReportSpec,
  widgetWorkspaceIds,
  type WidgetRenderContext,
} from '../../service/widgetSpec'
import DashboardChartSkeleton from '../skeletons/DashboardChartSkeleton.vue'

import OverviewChartMetricCard from './OverviewChartMetricCard.vue'

import {
  aggregateMultiMetricSeries,
  buildMultiMetricChartOptions,
  mapColumns,
  multiMetricDateKey,
  multiMetricDateRange,
  multiMetricPeriodOf,
  multiMetricResponseRows,
  multiMetricValueField,
  ReportDatePicker,
  ReportFilters,
  ReportToolbarSelect,
  toReportColumnOption,
  useMetricCatalog,
  useReportCellFormatter,
  useReportData,
  type MultiMetricFrequency,
  type MultiMetricSeriesMap,
  type ReportColumn,
  type ReportFilter,
  type ReportResponsePayload,
  type ReportSpec,
} from '@/features/reports'
import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'
import { Alert, Button, Icon, type SelectOption } from '@/shared/ui'
import { ChartRoot } from '@/shared/ui/chart'

/** Initial visible card count, matching summary (contract 28). */
const DEFAULT_VISIBLE_METRIC_COUNT = 4
/** Two initial series preserve the chart's left/right axes, matching summary. */
const DEFAULT_ACTIVE_METRIC_COUNT = 2

const props = withDefaults(
  defineProps<{
    widget: DashboardWidget
    title?: string
    wsId?: number | string | null
    accountWorkspaces?: readonly (number | string)[]
    targetCurrency?: string | null
    defaultPostData?: string
    defaultMetricFields?: readonly string[]
    calcRoasFirstMetric?: string
    hideDatePicker?: boolean
  }>(),
  {
    title: '',
    wsId: null,
    accountWorkspaces: () => [],
    targetCurrency: null,
    defaultPostData: '',
    defaultMetricFields: () => [],
    calcRoasFirstMetric: '',
    hideDatePicker: false,
  },
)
/**
 * `ReportSection.vue` renders a report-level dropdown filter (e.g. a "Branded/Non-branded"
 * campaign filter, `SearchAdsExcellence`'s `campaignOverviewBuild`) ABOVE whichever component a
 * section names as its `renderer` — for every OTHER renderer via a plain sibling div, and for
 * `DataVisible` via this exact slot. A custom renderer had no such slot, so the filter and this
 * widget's own Frequency/Platform selects ended up in two disconnected rows. Accepting the same
 * `#controls` slot here — and rendering it in the SAME row as Frequency/Platform below — lets
 * `ReportSection` hand it through instead of drawing its own separate row.
 */
defineSlots<{ controls?: () => unknown }>()
const { t } = useI18n()
const format = useFormat()
const catalog = useMetricCatalog()
const { formatCell } = useReportCellFormatter()
const frequency = ref<MultiMetricFrequency>('date')
const activePlatform = ref('all')
const activeMetricFields = ref<string[]>([])
/**
 * Which of the widget's configured metrics currently render as a card — a view preference, not
 * widget config: it never touches `widget.wire.setMetrics` and resets on reload, exactly like
 * summary's `selectedMetricFields` (session-local, `useSummaryOverview.ts`). Swap/remove/add all
 * operate on this list; the full configured set (`columns`) is only the catalog they pick from.
 */
const selectedMetricFields = ref<string[]>([])
/**
 * The ad-hoc filter box (`ReportFilters` — the same search-and-add-a-filter control every
 * `DataVisible` chart/table already has). `filterDraft` is what the editor shows; `activeUserFilters`
 * is what actually reaches the request, updated only on `@apply` — so typing a value does not
 * refetch on every keystroke. Session-local like everything else on this widget: it is layered
 * onto the widget's own configured `defaultFilters` (never replaces them) and is not persisted.
 */
const filterDraft = ref<ReportFilter[]>([])
const activeUserFilters = ref<ReportFilter[]>([])

function applyFilters(value: ReportFilter[]): void {
  filterDraft.value = value
  activeUserFilters.value = value
}

const frequencyOptions = computed<SelectOption<MultiMetricFrequency>[]>(() => [
  { value: 'date', label: t('dashboards.widget.daily') },
  { value: 'week', label: t('dashboards.widget.weekly') },
  { value: 'month_of_year', label: t('dashboards.widget.monthly') },
  { value: 'year', label: t('dashboards.widget.yearly') },
])
const context = computed<WidgetRenderContext>(() => ({
  defaultPostData: props.defaultPostData,
  wsId: props.wsId,
  accountWorkspaces: props.accountWorkspaces,
  targetCurrency: props.targetCurrency,
}))
const workspaceIds = computed(() => widgetWorkspaceIds(props.widget.wire, context.value))
/** Keyed date-range storage id (`useDateRange`) — distinct per widget instance, same pattern as
 *  `DashboardWidgetHost`'s own picker. UI only for now: its emitted range does not yet feed `spec`. */
const dateRangePickerId = computed(
  () => `overview-chart:${props.widget.wire.id ?? props.widget.wire.visibleType ?? 'unknown'}`,
)
const metricFields = computed(() =>
  (props.widget.wire.setMetrics ?? []).filter(
    (field): field is string => typeof field === 'string' && field.length > 0,
  ),
)
const columns = computed<ReportColumn[]>(() =>
  mapColumns(metricFields.value, { getColumn: catalog.getColumn }),
)
const requestMetrics = computed(() =>
  props.calcRoasFirstMetric && metricFields.value.includes('roas')
    ? [...new Set([...metricFields.value, props.calcRoasFirstMetric, 'cost'])]
    : metricFields.value,
)
const spec = computed<ReportSpec | null>(() =>
  widgetToReportSpec(props.widget, context.value, {
    dimensions: [frequency.value],
    metrics: requestMetrics.value,
    filters: [...widgetDefaultFilters(props.widget.wire), ...activeUserFilters.value],
    add_compare: true,
    pagination_enabled: false,
  }),
)
const report = useReportData(spec, { workspaceIds })
const payload = computed<ReportResponsePayload | null>(() => {
  const response = report.response.value
  return response?.combined ?? response ?? null
})
const rows = computed(() => multiMetricResponseRows(payload.value))
// The request's main range splits the rows into current and baseline; a baseline row is read from
// its `prev_<metric>` field (ORP-24984, v3 `OrphexDashboardChartArea.vue` `processData`).
const dateRange = computed(() => multiMetricDateRange(spec.value?.defaultPostData))
const series = computed<MultiMetricSeriesMap | null>(() =>
  aggregateMultiMetricSeries({
    rows: rows.value,
    metrics: requestMetrics.value,
    calcRoasFirstMetric: props.calcRoasFirstMetric,
    dateKeyOf: (row) => multiMetricDateKey(row, frequency.value),
    periodOf: (row, dateKey) =>
      multiMetricPeriodOf(row, dateKey, dateRange.value, frequency.value),
    valueFieldOf: multiMetricValueField,
    responseTotals: payload.value,
  }),
)
const platforms = computed(() => {
  if (!series.value) return []
  return [...new Set(Object.values(series.value).map((item) => item.platform))].sort((a, b) =>
    a === 'all' ? -1 : b === 'all' ? 1 : a.localeCompare(b),
  )
})
const platformOptions = computed(() =>
  platforms.value.map((platform) => ({
    value: platform,
    label: platform,
    ...(platform === 'all' ? {} : { meta: { visual: { platform } } }),
  })),
)

watch(platforms, (values) => {
  if (!values.includes(activePlatform.value)) activePlatform.value = values[0] ?? 'all'
})

/**
 * The schedule's highlighted metrics (`defaultMetricFields`) first, then whatever else is
 * configured — the same list drives which cards default to visible AND, sliced further, which
 * default to active. `metricFields` (`widget.wire.setMetrics`) is often far wider, so slicing it
 * unprioritized could open the rail on metrics the schedule never flagged.
 */
const prioritizedFields = computed(() => {
  const preferred = props.defaultMetricFields.filter((field) => metricFields.value.includes(field))
  const rest = metricFields.value.filter((field) => !preferred.includes(field))
  return [...preferred, ...rest]
})

// v3 `OrphexDashboardChart.changePlatform` opened `defaultMetrics.length` cards (else 4).
const defaultVisibleCount = computed(
  () => new Set(props.defaultMetricFields).size || DEFAULT_VISIBLE_METRIC_COUNT,
)

// Reseed rule, shared by the visible-card set and the active-series set below: keep whichever
// fields are still configured/visible, and only fall back to the prioritized front of the list
// once the selection empties out (initial mount, or every previously-shown metric was
// reconfigured away).
watch(
  metricFields,
  (fields) => {
    selectedMetricFields.value = selectedMetricFields.value.filter((field) =>
      fields.includes(field),
    )
    if (selectedMetricFields.value.length === 0) {
      selectedMetricFields.value = prioritizedFields.value.slice(0, defaultVisibleCount.value)
    }
  },
  { immediate: true },
)

/**
 * Active (plotted) is always a SUBSET of the visible cards — never a metric with no card at all —
 * exactly parity with summary's own `activeMetricFields = selectedMetricFields.slice(0, 2)`
 * (`useSummaryOverview.ts`). Watching `selectedMetricFields` rather than `metricFields` directly is
 * what keeps that subset relationship true after every reseed, not only on first mount.
 */
watch(
  selectedMetricFields,
  (visible) => {
    activeMetricFields.value = activeMetricFields.value.filter((field) => visible.includes(field))
    if (activeMetricFields.value.length === 0) {
      activeMetricFields.value = visible.slice(0, DEFAULT_ACTIVE_METRIC_COUNT)
    }
  },
  { immediate: true },
)

const visibleMetrics = computed(() =>
  selectedMetricFields.value.flatMap(
    (field) => columns.value.find((column) => column.field === field) ?? [],
  ),
)
/** Configured metrics with no card shown yet — what `+` reveals and what a swap can pick. */
const metricsAvailableToAdd = computed(() =>
  columns.value.filter((column) => !selectedMetricFields.value.includes(column.field)),
)

function metricOptions(metric: ReportColumn): SelectOption<string>[] {
  return [metric, ...metricsAvailableToAdd.value].map((item) => toReportColumnOption(item))
}

function changeMetric(previous: string, next: string): void {
  if (previous === next) return
  selectedMetricFields.value = selectedMetricFields.value.map((field) =>
    field === previous ? next : field,
  )
  activeMetricFields.value = activeMetricFields.value.map((field) =>
    field === previous ? next : field,
  )
}

function removeMetric(field: string): void {
  selectedMetricFields.value = selectedMetricFields.value.filter((item) => item !== field)
  activeMetricFields.value = activeMetricFields.value.filter((item) => item !== field)
}

function addMetric(): void {
  const metric = metricsAvailableToAdd.value[0]
  if (!metric) return
  selectedMetricFields.value = [...selectedMetricFields.value, metric.field]
}

function metricSeries(column: ReportColumn) {
  return series.value?.[`${activePlatform.value}_${column.field}`]
}
function toggleMetric(field: string): void {
  const active = activeMetricFields.value
  if (active.includes(field)) {
    if (active.length > 1) activeMetricFields.value = active.filter((item) => item !== field)
    return
  }
  activeMetricFields.value = [...active, field]
}
const chartOptions = computed(() =>
  buildMultiMetricChartOptions({
    frequency: frequency.value,
    previousPeriodLabel: t('dashboards.widget.previousPeriod'),
    forecastLabel: t('dashboards.widget.forecast'),
    formatDate: (value) => format.formatDate(value),
    series: columns.value.flatMap((column, index) => {
      const item = metricSeries(column)
      if (!item || !activeMetricFields.value.includes(column.field)) return []
      return [
        {
          field: column.field,
          label: column.header,
          color: `var(--ui-chart-summary-${(index % 5) + 1})`,
          current: item.current,
          previous: item.before,
          formatValue: (value: number | null | undefined) => formatCell(value, column, true),
          formatAxisValue: (value: number) => format.formatCompact(value, { maxFrac: 0 }),
        },
      ]
    }),
  }),
)

function downloadData(): WidgetSheet[] {
  const data = Array.isArray(payload.value?.data) ? payload.value.data : []
  return columns.value.length
    ? [{ name: props.title, data: [...data], cols: [...columns.value] }]
    : []
}

defineExpose({ downloadData })
</script>

<template>
  <div
    class="dashboard-overview flex flex-1 flex-col gap-sm min-h-80 p-2"
    data-testid="overview-chart-widget"
  >
    <DashboardChartSkeleton
      v-if="report.state.value === 'loading' || report.state.value === 'idle'"
      :label="title || t('dashboards.widget.untitled')"
      :cards="DEFAULT_VISIBLE_METRIC_COUNT"
    />
    <Alert v-else-if="report.state.value === 'error'" tone="warning">
      {{ t('reports.section.error') }}
      <Button size="sm" variant="outline" @click="report.reload">
        {{ t('reports.section.retry') }}
      </Button>
    </Alert>
    <div
      v-else-if="!series"
      class="dashboard-overview__empty grid flex-1 min-h-48 place-items-center text-muted"
    >
      {{ t('dashboards.widget.noData') }}
    </div>
    <template v-else>
      <div class="dashboard-overview__controls flex flex-wrap items-center gap-xs">
        <ReportFilters
          class="dashboard-overview__filters min-w-0 flex-auto max-md:basis-full"
          :columns="columns"
          :model-value="filterDraft"
          @update:model-value="filterDraft = $event"
          @apply="applyFilters"
        />
        <slot name="controls" />

        <ReportToolbarSelect
          v-model="frequency"
          :label="t('dashboards.widget.frequency')"
          :options="frequencyOptions"
          icon="repeat"
        />
        <ReportToolbarSelect
          v-if="platforms.length > 1"
          v-model="activePlatform"
          :label="t('dashboards.widget.platform')"
          :options="platformOptions"
          icon="circle-nodes"
        />
        <ReportDatePicker
          v-if="!hideDatePicker"
          :id="dateRangePickerId"
          class="dashboard-overview__date-picker w-[min(100%,_16rem)] flex-[0_1_16rem]"
        />
      </div>
      <div class="dashboard-overview__metrics flex gap-xs overflow-x-auto pb-2xs">
        <OverviewChartMetricCard
          v-for="(column, index) in visibleMetrics"
          :key="column.field"
          :metric="column"
          :options="metricOptions(column)"
          :active="activeMetricFields.includes(column.field)"
          :color="`var(--ui-chart-summary-${(index % 5) + 1})`"
          :current-total="metricSeries(column)?.current_total ?? 0"
          :previous-total="metricSeries(column)?.before_total ?? 0"
          :formatted-value="formatCell(metricSeries(column)?.current_total, column, true)"
          :remove-label="t('dashboards.widget.removeMetric', { metric: column.header })"
          @toggle="toggleMetric(column.field)"
          @remove="removeMetric(column.field)"
          @change="changeMetric(column.field, $event)"
        />
        <Button
          v-if="metricsAvailableToAdd.length"
          class="dashboard-overview__add-metric w-[3.625rem] min-w-[3.625rem] min-h-14 self-stretch border border-border rounded-md bg-surface shadow-pop"
          variant="ghost"
          square
          :aria-label="t('dashboards.widget.addMetric')"
          @click="addMetric"
        >
          <Icon name="plus" :size="16" />
        </Button>
      </div>
      <ChartRoot
        class="dashboard-overview__chart flex-1 min-h-100"
        :options="chartOptions"
        :load-error-message="t('reports.section.error')"
        :retry-label="t('reports.section.retry')"
        @retry="report.reload"
      />
    </template>
  </div>
</template>
